import { useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Dropdown,
  Form,
  FormButton,
  FormField,
  Icon,
  Input,
  Label,
  Popup,
} from "semantic-ui-react";
import { useMeetingNotesTemplates } from "../../../hooks/useMeetingNotesTemplates";
import { style } from "../../../style";
import { useAuth } from "../../../contexts/AuthContext";
import { getTimezoneOptions } from "../../../utils/timezoneOptions";

const UserForm = ({
  formErrors,
  userDetails,
  setUserDetails,
  handleSubmit,
  handleCancel,
  isLoading,
  editMode,
}) => {
  const { hasFeature } = useAuth();
  const { templates, isLoading: templatesLoading } = useMeetingNotesTemplates();
  // Zone as loaded, so a legacy saved zone stays pickable after changing it
  const [savedTimezone] = useState(userDetails?.user_timezone);
  const timezoneOptions = useMemo(
    () => getTimezoneOptions(savedTimezone),
    [savedTimezone],
  );
  const templateOptions = useMemo(
    () =>
      templates.map((template) => ({
        key: template.id,
        value: template.id,
        text: template.name,
      })),
    [templates],
  );

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div
      style={{
        maxWidth: "750px",
      }}
    >
      <Form onSubmit={handleSubmit} onKeyDown={handleKeyDown}>
        <FormField>
          <label>First name</label>
          {"first_name" in formErrors && (
            <Label color="red">First name is required</Label>
          )}
          <Input
            value={userDetails?.first_name ?? ""}
            disabled={editMode}
            onChange={(_, { value }) =>
              setUserDetails({ ...userDetails, first_name: value })
            }
          />
        </FormField>
        <FormField>
          <label>Last name</label>
          {"last_name" in formErrors && (
            <Label color="red">Last name is required</Label>
          )}
          <Input
            value={userDetails?.last_name ?? ""}
            disabled={editMode}
            onChange={(_, { value }) =>
              setUserDetails({ ...userDetails, last_name: value })
            }
          />
        </FormField>
        <FormField>
          <label>Email address</label>
          {"email" in formErrors && (
            <Label color="red">
              {formErrors.email[0] || "Email address is required"}
            </Label>
          )}
          <Input
            value={userDetails?.email ?? ""}
            disabled={editMode}
            onChange={(_, { value }) => {
              setUserDetails({ ...userDetails, email: value });
            }}
          />
        </FormField>
        <FormField>
          <label>iPECS extension</label>
          {"ipecs_ext" in formErrors && (
            <Label color="red">
              {formErrors.ipecs_ext[0] || "iPECS extension is required"}
            </Label>
          )}
          <Input
            value={userDetails?.ipecs_ext ?? ""}
            onChange={(_, { value }) => {
              setUserDetails({ ...userDetails, ipecs_ext: value });
            }}
          />
        </FormField>
        <FormField>
          <label>Timezone</label>
          <div style={{ marginBottom: "8px" }}>
            <small>
              Will default to the customer&rsquo;s timezone
              {userDetails?.customer_timezone &&
                ` (${userDetails.customer_timezone})`}{" "}
              unless you set a user-specific timezone here
            </small>
          </div>
          {"user_timezone" in formErrors && (
            <Label color="red">{formErrors.user_timezone[0]}</Label>
          )}
          <Dropdown
            search
            selection
            clearable
            options={timezoneOptions}
            value={userDetails?.user_timezone ?? ""}
            placeholder="Select a timezone"
            onChange={(_, { value }) => {
              setUserDetails({ ...userDetails, user_timezone: value || null });
            }}
          />
        </FormField>
        <FormField>
          <label>Meeting notes template</label>
          <div style={{ marginBottom: "8px" }}>
            <small>
              Used when this user's calls are summarised. Leave blank to use the
              customer default.
            </small>
          </div>
          {"meeting_notes_template_id" in formErrors && (
            <Label color="red">{formErrors.meeting_notes_template_id[0]}</Label>
          )}
          {/* Cleared/"" means "inherit the customer default" (sent as null),
              the same intent as UsersTable's INHERIT_DEFAULT option, but not
              the same as SummariseTemplatePicker's BUILT_IN, which forces the
              built-in prompt. "" is fine here because an empty value is
              exactly what a cleared dropdown should look like. */}
          <Dropdown
            search
            selection
            clearable
            loading={templatesLoading}
            disabled={templatesLoading}
            options={templateOptions}
            value={userDetails?.meeting_notes_template_id ?? ""}
            placeholder={
              templatesLoading ? "Loading templates…" : "Customer default"
            }
            onChange={(_, { value }) => {
              setUserDetails({
                ...userDetails,
                meeting_notes_template_id: value || null,
              });
            }}
          />
        </FormField>
        {hasFeature(["auto_transcribe"]) && (
          <FormField
            style={{
              display: "flex",
              alignItems: "center",
              alignContent: "center",
              gap: "5px",
              marginTop: "36px",
            }}
          >
            <Checkbox
              label="Auto transcription"
              checked={userDetails?.auto_transcribe ?? false}
              toggle
              onChange={(_, { checked }) => {
                setUserDetails({
                  ...userDetails,
                  auto_transcribe: checked,
                });
              }}
            />
            <Popup
              content="Automatically transcribe this user's calls."
              basic
              trigger={<Icon name="info circle" size="small" />}
            />
          </FormField>
        )}
        <FormField
          style={{
            display: "flex",
            alignItems: "center",
            alignContent: "center",
            gap: "5px",
            marginTop: "36px",
          }}
        >
          <Checkbox
            label="Admin user"
            checked={userDetails?.admin ?? false}
            toggle
            onChange={(_, { checked }) => {
              setUserDetails({ ...userDetails, admin: checked });
            }}
          />
          <Popup
            content="Admins can add, edit and delete users and view all call transcriptions and summaries. They can also manage teams and assign and revoke sidekick licences."
            basic
            trigger={<Icon name="info circle" size="small" />}
          />
        </FormField>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "end",
            marginTop: "40px",
            gap: "10px",
          }}
        >
          <Button onClick={handleCancel} disabled={isLoading}>
            Cancel
          </Button>
          <FormButton
            type="submit"
            style={style.gradientButton}
            disabled={isLoading}
            loading={isLoading}
          >
            Save
          </FormButton>
        </div>
      </Form>
    </div>
  );
};

export default UserForm;
